import {
  SessionExpiredError,
  type Auth0Session,
  type DevelopmentSession,
  type TraceSession,
} from '@traceio-org/auth-ui';

/** The parts of the sessions the hub uses (structural, so tests can pass doubles). */
export type HubAuth0Session = Pick<
  Auth0Session,
  'mode' | 'beginSignIn' | 'completeSignIn' | 'getAccessToken' | 'isAuthenticated' | 'signOut'
>;
export type HubDevSession = Pick<
  DevelopmentSession,
  'mode' | 'listFixtures' | 'signIn' | 'getAccessToken' | 'isAuthenticated' | 'signOut'
>;

/**
 * In-memory record of how the person signed in to the hub (Auth0 or development identity). Nothing is
 * persisted: after a reload the store probes the sessions again, and when none answers the page shows
 * the session-expired state.
 */
export class SessionStore {
  readonly auth0: HubAuth0Session | null;
  readonly dev: HubDevSession | null;
  private readonly audience: string;
  private current: TraceSession | null = null;
  private restoring: Promise<TraceSession | null> | null = null;

  constructor(auth0: HubAuth0Session | null, dev: HubDevSession | null, audience: string) {
    this.auth0 = auth0;
    this.dev = dev;
    this.audience = audience;
  }

  active(): TraceSession | null {
    return this.current;
  }

  activate(session: TraceSession): void {
    this.current = session;
    this.restoring = null;
  }

  clear(): void {
    this.current = null;
    this.restoring = null;
  }

  /** Returns the active session, or finds a live one (Auth0 first, then development identity). */
  restore(): Promise<TraceSession | null> {
    if (this.current) return Promise.resolve(this.current);
    this.restoring ??= this.probe().then((session) => {
      if (session) this.current = session;
      return session;
    });
    return this.restoring;
  }

  private async probe(): Promise<TraceSession | null> {
    for (const session of [this.auth0, this.dev]) {
      if (!session) continue;
      try {
        if (await session.isAuthenticated()) return session;
      } catch (error) {
        console.warn(`account-hub: could not check the ${session.mode} session`, error);
      }
    }
    return null;
  }

  /** Token for platform-api calls; SessionExpiredError when nobody is signed in. */
  async getAccessToken(): Promise<string> {
    const session = this.current ?? (await this.restore());
    if (!session) throw new SessionExpiredError('no_session');
    return session.getAccessToken(this.audience);
  }
}
